import { objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { capitalize, cached } from '../../../utils/utils.js';
import { warn } from '../../../utils/console.js';
import get from 'lodash/get.js';

const normalizeValue = cached(value => {
  return value.toString().toLowerCase().trim();
});
let columnId = 0;
function findRowLocation(row, _ref) {
  let {
    rowsMap,
    rowKey,
    groupConfig,
    treeConfig
  } = _ref;
  if (rowsMap.has(row[rowKey])) {
    return {
      key: row[rowKey]
    };
  }
  const isRowGroupingEnabled = groupConfig && groupConfig.enabled;
  const isRowTreeEnabled = treeConfig && treeConfig.children;
  if (isRowGroupingEnabled || isRowTreeEnabled) {
    const childKey = isRowTreeEnabled ? this.treeConfig.children : 'children';
    return findNestedRowLocation(row, [...rowsMap.values()], rowKey, childKey);
  }
  return null;
}
function findNestedRowLocation(row, rowsData, rowKey, childKey) {
  for (const parentRow of rowsData) {
    if (parentRow[childKey]) {
      const targetRowIndex = parentRow[childKey].findIndex(childRow => childRow[rowKey] === row[rowKey]);
      if (targetRowIndex > -1) {
        return {
          rowSet: parentRow[childKey],
          targetRowIndex
        };
      }
      const nestedRow = findNestedRowLocation(row, parentRow[childKey], rowKey, childKey);
      if (nestedRow) {
        return nestedRow;
      }
    }
  }
  return null;
}
function flattenChildRows(data, opts) {
  const ariaLevel = opts.ariaLevel || 1;
  return data.reduce((acc, row, idx) => {
    acc.push(_objectSpread2(_objectSpread2({}, row), {}, {
      ariaLevel,
      ariaSetSize: data.length,
      ariaPosInSet: idx + 1
    }));
    if (row[opts.childKey] && opts.childRowFilter(row)) {
      acc.push(...flattenChildRows(row[opts.childKey], _objectSpread2(_objectSpread2({}, opts), {}, {
        ariaLevel: ariaLevel + 1
      })));
    }
    return acc;
  }, []);
}
function capitalizeColumnLabel(str) {
  str = str.split(' ');
  for (let i = 0, x = str.length; i < x; i++) {
    str[i] = capitalize(str[i]);
  }
  return str.join(' ');
}
function getColumnConfig(column) {
  if (['tree', 'checkbox', 'index'].includes(column.type)) {
    column = _objectSpread2(_objectSpread2({}, column), {}, {
      width: column.width || 36,
      widthType: 'fixed',
      sortable: false,
      visible: true,
      downloadable: false,
      allowResize: false,
      allowReorder: false
    });
  }
  if (column.type === 'actions') {
    column.allowResize = false;
    column.allowReorder = false;
    column.downloadable = false;
  }
  if (column.label && !column.width) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '700 14px "Inter", Helvetica Neue, Arial, sans-serif';
    const label = capitalizeColumnLabel(column.label);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let {
      width
    } = ctx.measureText(label);
    column.labelWidth = width;
    width += 32;
    if (column.sortable) {
      width += 26;
    }
    if (column.filters) {
      width += 26;
    }
    let minWidth;
    if (column.minWidth) {
      minWidth = Math.max(column.minWidth, Math.ceil(width));
    } else {
      minWidth = Math.min(Math.ceil(width), 280);
    }
    column.minWidth = minWidth;
    column.widthType = column.widthType || 'flex';
  }
  if (!column.width && !column.minWidth) {
    warn(`no width or minWidth detected for column '${column.prop}'`);
    column.minWidth = 36;
    column.widthType = 'flex';
  }
  if (column.width) {
    column.widthType = 'fixed';
  }
  return {
    align: column.align,
    allowResize: column.allowResize !== false,
    allowReorder: column.allowReorder !== false,
    append: column.append,
    async: !!column.async,
    colSpan: 1,
    columnKey: column.columnKey || column.prop || column.property || column.type,
    downloadable: column.downloadable !== false,
    exact: column.exact,
    filterMethod: column.filterMethod,
    filterMultiple: column.filterMultiple,
    filters: (column.filters || []).map(_ref2 => {
      let {
        label,
        value
      } = _ref2;
      return {
        prop: column.prop,
        label,
        value,
        checked: false
      };
    }),
    fixed: column.fixed === true || column.fixed === 'left' ? 'left' : column.fixed,
    fixedPos: 0,
    fixedShadow: false,
    formatter: column.formatter,
    headerAlign: column.headerAlign,
    href: column.href,
    id: `col--${++columnId}`,
    indexMethod: column.indexMethod,
    label: column.label,
    labelWidth: column.labelWidth || column.minWidth,
    level: 1,
    minWidth: column.minWidth || 120,
    order: column.sortable ? column.order || 'none' : null,
    property: column.prop || column.property,
    renderHeight: 0,
    renderWidth: 0,
    replace: column.replace,
    resizeWidth: column.resizeWidth || 0,
    rowSpan: 1,
    showOverflow: column.showOverflow === false ? false : column.showOverflow || column.showOverflowTooltip || true,
    slot: column.slot,
    sortable: column.sortable,
    sortBy: column.sortBy,
    sortMethod: column.sortMethod,
    target: column.target,
    to: column.to,
    tooltipFormatter: column.tooltipFormatter,
    treeNode: column.treeNode,
    type: column.type,
    visible: column.visible,
    width: column.width,
    widthType: column.widthType
  };
}
function getProp(target, key) {
  return get(target, key);
}
function emitEvent(_vm, type, args) {
  if (_vm.$listeners[type] || _vm._events[type]) {
    _vm.$emit(...[type].concat(args));
  }
}

export { capitalizeColumnLabel, emitEvent, findNestedRowLocation, findRowLocation, flattenChildRows, getColumnConfig, getProp, normalizeValue };
//# sourceMappingURL=utils.js.map
